/**
 * =============================================================================
 * product-form.js
 * =============================================================================
 * Manage state of a single product form
 * =============================================================================
 */

import VariantStateManager from './variant-state-manager'

class ProductForm {
  container;
  form;

  variantStateManager;
  swiperMedia; // Injected in a pre-init callback from product-media.liquid
  swiperThumbs; // Injected in a pre-init callback from product-media.liquid

  quantity;

  constructor(container) {
    this.container = container;

    this.form = this.container.querySelector('.product-form');
    this.form.querySelector('[name=id]').disabled = false;
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

    let variantSelect = this.container.querySelector("#variant-select");
    let sellingPlanSelect = this.container.querySelector("#selling-plan-select");
    this.variantStateManager = new VariantStateManager(this.container, variantSelect, sellingPlanSelect);

    this.product = JSON.parse(this.container.querySelector("[data-product-json]").innerText);	

    this.container.addEventListener('variant:changed', this.onVariantChanged.bind(this));

    this.quantity = this.container.querySelector("[name='quantity']");
    this.quantity.addEventListener("change", e => {
      let event = new CustomEvent("quantity:changed", { detail: {
        variant: this.variantStateManager.currentVariant,
        quantity: e.currentTarget.value
      }});
      this.container.dispatchEvent(event);
    });

    // Handle sticky add button
    if(this.container.querySelector("#sticky-add")){
      this.container.querySelector("#sticky-add").addEventListener("click", () => this.form.dispatchEvent(new CustomEvent('submit', {cancelable: true})));
    }
  }

  onSubmitHandler(e) {
    e.preventDefault();

    // Prevent further submissions if we're in the middle of one already
    const submitButton = this.form.querySelector('[type="submit"]');
    if (submitButton.classList.contains('loading')) return;

    // Update visual state as we process the submission. There might be multiple
    // add buttons on the page - this disables them all.
    this.container.querySelectorAll('[data-disable-on-add]').forEach(element => {
      element.classList.add('loading');
      element.setAttribute('aria-disabled', true);
    });
    this.container.querySelectorAll('.spinner-overlay').forEach(element => element.classList.remove('d-none'));

    // Construct the submission body - this uses FormData, which will get every
    // form field in the form (including 'properties')
    const formData = new FormData(this.form);
    formData.append('sections_url', window.location.pathname);
    const selectedPurchaseTypeElement = this.form.querySelector('[data-purchase-type-radio]:checked');
    const selectedPurchaseType = selectedPurchaseTypeElement ? selectedPurchaseTypeElement.value : null;
    if(selectedPurchaseType === "one-time"){
      formData.delete('selling_plan');
    }

    // Remove any form inputs with an empty string value
    let emptyKeys = [];
    for (const pair of formData.entries()) {
      if(pair[1] === ""){
        emptyKeys.push(pair[0]);
      }
    }
    emptyKeys.forEach(emptyKey => {
      formData.delete(emptyKey);
    });

    // Certain apps (e.g. GiftShip) expect the product form to handle adding
    // their additional products. However, this won't work with FormData when
    // the 'primary' product has a selling plan, as the additional products
    // won't have a selling plan, and add.js will try and use the same selling
    // plan ID for all IDs. So in those cases, we need to strip it out into a 
    // standard variant items array for cart.js
    let variantIds = [];
    let sellingPlan = false;
    let properties = {};
    for (const pair of formData.entries()) {
      if(pair[0] == "id[]") {
        variantIds.push(pair[1]);
      }
      if(pair[0] == "selling_plan") {
        sellingPlan = pair[1];
      }
      if(pair[0].startsWith("properties")) {
        properties[pair[0].replace( /(^.*\[|\].*$)/g, '' )] = pair[1];
      }
    }

    // Check if we've found more than 1 variant ID, AND a selling plan. If so,
    // we need to create a standard items array of variants to add.
    let items = false;
    if(variantIds.length > 1 && sellingPlan){
      let primaryVariantId = variantIds.shift();
      items = [{"id": primaryVariantId, "quantity": formData.get("quantity"), "selling_plan": sellingPlan, "properties": properties }];
      variantIds.forEach(id => {
        items.push({"id": id, "quantity": formData.get("quantity")});
      });
    }

    // Determine whether to send items, or formData
    let dataToSend = items ? items : formData;

    // Add the submitted product to the cart
    MP.Extensions.Cart.add(dataToSend).finally(e => {
      this.container.querySelectorAll('[data-disable-on-add]').forEach(element => {
        element.classList.remove('loading');
        element.removeAttribute('aria-disabled');
      });
      
      this.container.querySelectorAll('.spinner-overlay').forEach(element => element.classList.add('d-none'));
    });
  }

  onVariantChanged(e) {
    console.log('[P-F] Variant changed on this product form');
    const variant = this.variantStateManager.currentVariant;

    // Update Swiper current slide
    const matchingSlide = this.container.querySelector(`[data-variants*='${variant.id}']:not(.swiper-slide-duplicate)`);
    if (matchingSlide) {
      const slideIndex = matchingSlide.dataset.index;
      if (slideIndex) {
        this.swiperMedia.slideToLoop(parseInt(slideIndex));
      }
    }

    // Update Klarna
    window.Klarna?.OnsiteMessaging.refresh();

    // Update pickup availability
    this.refreshPickupAvailability(variant.id);
  }

  refreshPickupAvailability(variantId) {
    fetch(window.Shopify.routes.root + "variants/" + variantId + "/?section_id=pickup-availability")
      .then(response => response.text())
      .then(text => {
        const container = document.querySelector('[data-store-availability-container]');
        if(container) {
          const pickupAvailabilityHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('.pickup-availability-preview');

          container.innerHTML = '';
          if(pickupAvailabilityHTML){
            container.appendChild(pickupAvailabilityHTML);
          }
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  handleErrorMessage(message) {
    MP.Utilities.toast("warning", message, "fal fa-exclamation-triangle");
  }
}

export default ProductForm;